import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, MaskedLines } from "../components/Motion";
import { ParallaxHeroImage } from "../components/ParallaxImage";
import { CONSULTING_CONTENT } from "../i18n/consultingContent";
import { IMAGES } from "../lib/images";
import Seo from "../components/Seo";
import AnswerLead from "../components/AnswerLead";
import FaqSection from "../components/FaqSection";
import Newsletter from "../components/Newsletter";

const OfferCard = ({ offer }) => (
  <div className="border border-white/10 bg-obsidian-soft p-8 md:p-12" data-testid="consulting-offer">
    <h3 className="font-serif text-3xl font-light text-white md:text-4xl">{offer.title}</h3>
    <p className="mt-6 text-base font-light leading-relaxed text-white/60">{offer.intro}</p>
    <ul className="mt-8 grid gap-x-8 gap-y-4 sm:grid-cols-2">
      {offer.points.map((p, i) => (
        <li key={i} className="flex items-start gap-3">
          <Check size={15} className="mt-1 shrink-0 text-champagne" />
          <span className="text-sm font-light text-white/75">{p}</span>
        </li>
      ))}
    </ul>
    <p className="mt-8 border-l-2 border-champagne pl-5 font-serif text-lg font-light italic leading-snug text-white/85 md:text-xl">
      {offer.note}
    </p>
  </div>
);

const Consulting = () => {
  const { t, lang } = useLang();
  const c = CONSULTING_CONTENT[lang];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "MYSYMP AG — Consulting",
    url: "https://mysymp.ch/consulting",
    description: {
      de: "Verwaltungsratsmandate (VR), strategisches Sparring und Executive Coaching – gestützt auf über 35 Jahre Führungserfahrung.",
      en: "Board of directors mandates, strategic sparring and executive coaching – based on over 35 years of leadership experience.",
      fr: "Mandats d'administration, sparring stratégique et coaching exécutif – fondés sur plus de 35 ans d'expérience de direction.",
    }[lang],
    provider: { "@type": "Organization", name: "MYSYMP AG", url: "https://mysymp.ch" },
    areaServed: { "@type": "Country", name: "Switzerland" },
    availableLanguage: ["de", "en", "fr"],
    serviceType: ["Board mandate", "Strategic sparring", "Executive coaching"],
  };

  return (
    <div data-testid="consulting-page" className="bg-obsidian">
      <Seo pageKey="consulting" schema={[serviceSchema]} />
      {/* Hero */}
      <section className="relative flex min-h-[80vh] items-end overflow-hidden pt-28">
        <ParallaxHeroImage src={IMAGES.consulting} alt={t.nav.consulting}>
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/55 to-obsidian/45" />
        </ParallaxHeroImage>
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 pb-16 md:px-12 md:pb-24">
          <div className="flex flex-wrap gap-3">
            {c.pills.map((p, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.1, duration: 0.7 }}
                className="border border-champagne/50 px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-champagne"
              >
                {p}
              </motion.span>
            ))}
          </div>
          <h1 className="mt-8 block max-w-4xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-white md:text-6xl">
            <MaskedLines lines={[c.headline]} startDelay={0.35} lineClass="pb-[0.14em]" />
          </h1>
        </div>
      </section>

      <AnswerLead pageKey="consulting" />


      {/* Intro + Profile */}
      <section className="border-t border-white/10 bg-obsidian py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] gap-16 px-6 md:grid-cols-12 md:px-12">
          <div className="md:col-span-7">
            {c.intro.map((para, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <p className={`mb-6 font-light leading-relaxed ${i === 1 ? "font-serif text-2xl italic text-champagne md:text-3xl" : "text-base text-white/70 md:text-lg"}`}>
                  {para}
                </p>
              </Reveal>
            ))}
          </div>

          {/* Profile box */}
          <div className="md:col-span-5">
            <Reveal delay={0.1}>
              <div className="border border-champagne/25 bg-obsidian-soft" data-testid="consulting-profile">
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img src={IMAGES.founder} alt="Isidoro Celentano" className="h-full w-full object-cover object-[72%_top]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian-soft via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 p-6">
                    <div className="font-serif text-3xl font-light text-white">Isidoro Celentano</div>
                    <div className="mt-1 text-xs uppercase tracking-[0.2em] text-champagne">{c.profile.role}</div>
                  </div>
                </div>
                <ul className="divide-y divide-white/10">
                  {c.profile.stats.map((s, i) => (
                    <li key={i} className="flex items-start gap-3 px-6 py-4">
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 bg-champagne" />
                      <span className="text-sm font-light leading-snug text-white/75">{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Offers */}
      <section className="border-t border-white/10 bg-obsidian-soft py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-champagne">{c.offerTitle}</span>
          </Reveal>
          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            {c.offers.map((o, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <OfferCard offer={o} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Background */}
      <section className="border-t border-white/10 bg-obsidian py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-6 md:grid-cols-12 md:px-12">
          <div className="md:col-span-4">
            <Reveal>
              <h2 className="font-serif text-3xl font-light text-white md:text-5xl">{c.backgroundTitle}</h2>
            </Reveal>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            {c.backgroundText.map((para, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <p className="mb-6 text-base font-light leading-relaxed text-white/70 md:text-lg">{para}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Approach + Audience */}
      <section className="border-t border-white/10 bg-obsidian-soft py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] gap-16 px-6 md:grid-cols-2 md:px-12">
          <Reveal>
            <h2 className="font-serif text-3xl font-light text-white md:text-4xl">{c.approachTitle}</h2>
            <p className="mt-6 text-base font-light leading-relaxed text-white/60">{c.approachIntro}</p>
            <ul className="mt-8 space-y-4">
              {c.approachPoints.map((p, i) => (
                <li key={i} className="flex items-start gap-3 border-b border-white/10 pb-4">
                  <Check size={15} className="mt-1 shrink-0 text-champagne" />
                  <span className="text-base font-light text-white/80">{p}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-3xl font-light text-white md:text-4xl">{c.audienceTitle}</h2>
            <p className="mt-6 text-base font-light leading-relaxed text-white/60">{c.audienceIntro}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {c.audience.map((p, i) => (
                <span key={i} className="border border-white/15 px-4 py-2.5 text-sm font-light text-white/75">
                  {p}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* marKIpilot */}
      <section className="relative overflow-hidden border-t border-white/10 bg-obsidian py-24 md:py-32" data-testid="markipilot-section">
        <div className="pointer-events-none absolute right-0 top-0 h-[380px] w-[560px] rounded-full bg-champagne/5 blur-[120px]" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <Reveal>
              <span className="text-xs uppercase tracking-[0.3em] text-champagne">{c.markipilot.overline}</span>
              <h2 className="mt-5 max-w-2xl font-serif text-4xl font-light leading-tight text-white md:text-6xl">{c.markipilot.title}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <a
                href={c.markipilot.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="markipilot-link"
                className="group inline-flex shrink-0 items-center gap-3 border border-champagne px-7 py-3.5 text-xs uppercase tracking-[0.2em] text-champagne transition-colors duration-300 hover:bg-champagne hover:text-obsidian"
              >
                {c.markipilot.cta}
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <p className="mt-8 max-w-3xl text-base font-light leading-relaxed text-white/65 md:text-lg">{c.markipilot.intro}</p>
          </Reveal>

          <div className="mt-12 grid grid-cols-3 gap-6 border-y border-white/10 py-8">
            {c.markipilot.stats.map((s, i) => (
              <Reveal key={i} delay={i * 0.08} className="text-center md:text-left">
                <div className="font-serif text-4xl font-light text-champagne md:text-6xl">{s.v}</div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-white/50">{s.l}</div>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 grid gap-x-10 gap-y-8 md:grid-cols-2 lg:grid-cols-3">
            {c.markipilot.services.map((s, i) => (
              <Reveal key={i} delay={(i % 3) * 0.08}>
                <div className="border-t border-white/10 pt-6" data-testid={`markipilot-service-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-lg italic text-champagne">0{i + 1}</span>
                    <h3 className="font-serif text-xl font-light text-white md:text-2xl">{s.t}</h3>
                  </div>
                  <p className="mt-3 text-sm font-light leading-relaxed text-white/55">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 bg-obsidian py-28 text-center md:py-40">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-4xl font-light leading-tight md:text-6xl">{c.ctaTitle}</h2>
            {c.ctaText.map((para, i) => (
              <p key={i} className="mx-auto mt-6 max-w-xl text-base font-light leading-relaxed text-white/60 md:text-lg">{para}</p>
            ))}
            <Link
              to="/contact"
              data-testid="consulting-cta-contact"
              className="group mt-10 inline-flex items-center gap-3 bg-champagne px-8 py-4 text-xs uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-champagne-light"
            >
              {t.nav.book}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <FaqSection pageKey="consulting" />

      <Newsletter />
    </div>
  );
};

export default Consulting;
